/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import BackButton from '../common/BackButton';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import '../bids/CreateBid.css';

const ManagerParticipantForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const { currentUser } = useAuth();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    title: '',
    contactNote: '',
    userInactive: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isEditMode) {
      loadUser();
    }
  }, [id]);

  const loadUser = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.getManagedUserById(id!);

      if (!response.success || !response.participant) {
        setError('Failed to load user');
        return;
      }

      const participant = response.participant;
      setFormData({
        username: participant.username || '',
        email: participant.email || '',
        firstName: participant.firstName || '',
        lastName: participant.lastName || '',
        title: participant.title || '',
        contactNote: participant.contactNote || '',
        userInactive: participant.userInactive || false,
      });
    } catch (err: any) {
      console.error('Error loading user:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsSaving(true);

    try {
      if (isEditMode) {
        // Update existing user
        const request = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          title: formData.title,
          contactNote: formData.contactNote,
          userInactive: formData.userInactive,
        };

        const response = await apiClient.updateManagedUser(id!, request);

        if (response.success) {
          setSuccessMessage('User updated successfully');
          setTimeout(() => navigate('/participants'), 1500);
        } else {
          setError(response.error || 'Failed to update user');
        }
      } else {
        // Create new user
        const request = {
          username: formData.username,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          title: formData.title,
          contactNote: formData.contactNote,
          // Manager ID will be set automatically by backend
          // Password will be set via email link - no need to provide it here
        };

        const response = await apiClient.createUser(request);

        if (response.success) {
          setSuccessMessage('User created successfully');
          setTimeout(() => navigate('/participants'), 1500);
        } else {
          setError(response.error || 'Failed to create user');
        }
      }
    } catch (err: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} user:`, err);
      setError(err.response?.data?.error || err.message || `An error occurred while ${isEditMode ? 'updating' : 'creating'} user`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/participants');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (isLoading) {
    return (
      <div className="create-bid-container">
      <BackButton />
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading user...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="create-bid-container">
      <div className="create-bid-header">
        <h2>{isEditMode ? 'Edit User' : 'Create User'}</h2>
      </div>

      {!isEditMode && currentUser?.demoParticipant && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '6px',
          color: '#856404'
        }}>
          <strong>Demo Account Restriction:</strong> Demo users cannot create new participants. This form is read-only for demonstration purposes.
        </div>
      )}

      <form onSubmit={handleSubmit} className="create-bid-form">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {successMessage && (
          <div style={{
            padding: '1rem',
            marginBottom: '1rem',
            backgroundColor: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '6px',
            color: '#155724'
          }}>
            {successMessage}
          </div>
        )}

        {isEditMode && (
          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              value={formData.username}
              readOnly
              disabled
              style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
            />
          </div>
        )}

        {!isEditMode && (
          <>
            <div className="form-group">
              <label htmlFor="username">Username *</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                disabled={isSaving}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isSaving}
              />
              <small style={{ color: '#666', fontSize: '0.85rem' }}>
                A welcome email with password setup link will be sent to this address
              </small>
            </div>
          </>
        )}

        <div className="form-group">
          <label htmlFor="firstName">First Name *</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            disabled={isSaving}
          />
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name *</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            disabled={isSaving}
          />
        </div>

        <div className="form-group">
          <label htmlFor="title">Title</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            disabled={isSaving}
          />
        </div>

        <div className="form-group">
          <label htmlFor="contactNote">Contact Notes</label>
          <textarea
            id="contactNote"
            name="contactNote"
            value={formData.contactNote}
            onChange={handleChange}
            rows={4}
            disabled={isSaving}
            style={{ minHeight: '100px', resize: 'vertical' }}
          />
        </div>

        {isEditMode && (
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="userInactive"
                checked={formData.userInactive}
                onChange={handleChange}
                disabled={isSaving}
                style={{ marginRight: '0.5rem' }}
              />
              Mark as Inactive
            </label>
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving}
            className="cancel-button"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="submit-button"
          >
            {isSaving ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create User')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ManagerParticipantForm;
